import {
	BSOL_STATS_API_RESPONSE,
	fetchBSolMetrics,
	fetchJitoSolMetrics,
	fetchMSolMetrics,
} from '@drift-labs/sdk';
import { useEffect, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { derr, dlog } from '../dev';
import useIsMainnet from './useIsMainnet';

type AprDetails = {
	source: string;
	apr: number;
	icon?: string;
};

type AprBreakdown = Record<number, AprDetails[]>;

const ALREADY_FETCHED_REF = { current: false };

function useAprBreakdowns(): AprBreakdown {
	const [bsolMetrics, setBSolMetrics] = useState<AprDetails[]>([]);
	const [jitoSolMetrics, setJitoSolMetrics] = useState<AprDetails[]>([]);
	const [msolMetrics, setMSolMetrics] = useState<AprDetails[]>([]);
	const isMainnet = useIsMainnet();

	useEffect(() => {
		if (!isMainnet) return;

		if (ALREADY_FETCHED_REF.current) {
			// Should only fetch apr metrics once per session otherwise we can get rate limited/blocked.
			derr(`apr_metrics_already_fetched`);
		}

		fetchAndSetMsolMetrics();
		fetchAndSetJitoSolMetrics();
		fetchAndSetBSolMetrics();
		ALREADY_FETCHED_REF.current = true;
	}, [isMainnet]);

	async function fetchAndSetBSolMetrics() {
		const statsResponse = await fetchBSolMetrics();
		if (statsResponse.status === 200) {
			const data = (await statsResponse.json()) as BSOL_STATS_API_RESPONSE;
			const baseApy = data?.stats?.apy.base;
			const lendingMultiplier = data?.stats?.apy.lending;
			const blzeApy = data?.stats?.apy.blze * lendingMultiplier;

			setBSolMetrics([
				{
					source: 'bSOL APY',
					apr: baseApy,
				},
				{
					source: 'BLZE Rewards',
					apr: blzeApy,
					icon: '/assets/icons/blze.svg',
				},
			]);
		}
	}

	async function fetchAndSetJitoSolMetrics() {
		dlog(`apr_metrics`, `fetching_jito_sol_metrics`);

		const data = await fetchJitoSolMetrics();

		const past30DaysApyAvg =
			(data.apy.slice(-30).reduce((a, b) => a + b.data, 0) / 30) * 100;

		setJitoSolMetrics([
			{
				source: 'JitoSOL APY',
				apr: past30DaysApyAvg,
			},
		]);
	}

	async function fetchAndSetMsolMetrics() {
		const data = await fetchMSolMetrics();

		setMSolMetrics([
			{
				source: 'mSOL APY',
				apr: data.msol_price_apy_30d,
				icon: '/assets/icons/mnde.png',
			},
		]);
	}

	if (!isMainnet) {
		return {};
	}

	return {
		2: msolMetrics,
		6: jitoSolMetrics,
		8: bsolMetrics,
	};
}

export default singletonHook({}, useAprBreakdowns);
